import { useTranslation } from '@mint-lib/i18n';
import {
  Checkbox,
  itemVariants,
  Label,
  TruncateText,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { splitFileSystemNodes } from '../../../helpers/knowledge-base/fileSystemNode.js';
import type { FileSystemNode, FolderNode } from '../../../types/fileSystem.js';
import { FileIcon } from '../../KnowledgeBase/FileSystem/FileIcon/FileIcon.jsx';
import { NavigationFolderItem } from '../../KnowledgeBase/FileSystem/FolderSection/FolderItem.jsx';

const getIsAllChecked = (items: CheckboxItem[]): boolean | 'indeterminate' => {
  const checkedCount = items.filter((item) => item.checked).length;

  if (checkedCount === 0) {
    return false;
  }

  if (checkedCount === items.length) {
    return true;
  }

  return 'indeterminate';
};

export type CheckboxItem = {
  id: string;
  label: string;
  checked: boolean;
};

type Props = {
  items: Array<FileSystemNode & CheckboxItem>;
  onItemChange: (
    id: string,
    checked: boolean,
    kind: FileSystemNode['kind'],
  ) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onOpen?: (folder: FolderNode) => void;
};

export const CheckboxGroup = ({
  items,
  onItemChange,
  onSelectAll,
  onDeselectAll,
  onOpen,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const selectAllState: boolean | 'indeterminate' = getIsAllChecked(items);

  const { files, folders } = splitFileSystemNodes(items);

  const handleSelectAllChange = (checked: boolean | 'indeterminate') => {
    if (checked === true || checked === 'indeterminate') {
      onSelectAll();
    } else {
      onDeselectAll();
    }
  };

  return (
    <div>
      <Label
        htmlFor="select-all"
        variant="bodyLong01"
        className={cn(
          itemVariants({ popoverItem: 'default' }),
          'flex items-center gap-3',
        )}
      >
        <Checkbox
          id="select-all"
          checked={selectAllState}
          onCheckedChange={handleSelectAllChange}
        />
        {t('Select all')}
      </Label>

      <div className="flex flex-col">
        {folders.map((item) => (
          <Label
            key={item.id}
            htmlFor={item.id}
            variant="bodyLong01"
            className={cn(
              itemVariants({ popoverItem: 'default' }),
              'flex items-center gap-2 pl-6',
            )}
          >
            <Checkbox
              id={item.id}
              checked={item.checked}
              onCheckedChange={
                (checked) =>
                  onItemChange(item.id, checked as boolean, item.kind) // Folders can have duplicate names
              }
            />
            <NavigationFolderItem folder={item} onOpen={onOpen} />
          </Label>
        ))}
        {files.map((item) => (
          <Label
            key={item.id}
            htmlFor={item.id}
            variant="bodyLong01"
            className={cn(
              itemVariants({ popoverItem: 'default' }),
              'flex items-center gap-2 pl-6',
            )}
          >
            <Checkbox
              id={item.id}
              checked={item.checked}
              onCheckedChange={(checked) =>
                onItemChange(item.label, checked as boolean, item.kind)
              }
            />
            <FileIcon type={item.fileType} />
            <TruncateText>{item.label}</TruncateText>
          </Label>
        ))}
      </div>
    </div>
  );
};
